import type { GameContext } from '../core/context';
import { distance, type V2 } from '../core/math';
import { Rng, hashString } from '../core/rng';
import { VEHICLE_SPECS } from '../vehicles/specs';
import type { Vehicle, VehicleDamage } from '../vehicles/types';
import type { VehicleService } from '../vehicles/vehicleSystem';
import type { ParkedSpot } from '../world/types';
import type { ParkedService } from './types';
import type { ViewTester } from './visibility';

/**
 * Parked cars at the layout's `parkedSpots`, streamed in and out around the focus so only ~30 are
 * ever alive. Each spot's car (type, paint, locked flag) is a pure function of the spot index and
 * the world seed, so walking away and coming back shows the same car — including accumulated
 * damage. A car that is stolen, moved or destroyed leaves its spot vacant for good.
 */

const SPAWN_R = 84;
const DESPAWN_R = 112;
const MAX_ACTIVE = 34;
const LOCKED_CHANCE = 0.42;
/** At game start the kerbs within this radius of the player's spawn are left empty (room to take the first car). */
const START_CLEAR_RADIUS = 26;

interface SpotState {
  specId: string;
  paint: number;
  locked: boolean;
  vehicle: Vehicle | null;
  vacant: boolean;
  /** Persisted damage between stream-outs. */
  health: number | null;
  damage: VehicleDamage | null;
}

export class ParkedManager implements ParkedService {
  private readonly spots: ParkedSpot[];
  private readonly state: SpotState[] = [];
  private activeCount = 0;
  private step = 0;

  constructor(
    ctx: GameContext,
    private readonly vehicles: VehicleService,
    spots: readonly ParkedSpot[],
    private readonly view: ViewTester,
    private readonly focus: () => V2,
    private readonly startPos: V2 | null = null,
    /** Spots where a car would sit in a turning path (e.g. on a corner); they stay empty. */
    noParking: ((pos: V2) => boolean) | null = null,
  ) {
    this.spots = spots.slice();
    const civilian = Object.values(VEHICLE_SPECS).filter((s) => s.trafficWeight > 0);
    const weights = civilian.map((s) => s.trafficWeight);
    for (let i = 0; i < this.spots.length; i++) {
      const spot = this.spots[i]!;
      const rng = new Rng(hashString(`parked:${i}`) ^ ctx.rng.seed);
      const specId = spot.allowedTypes.length > 0 ? rng.pick(spot.allowedTypes) : rng.weighted(civilian, weights).id;
      const spec = VEHICLE_SPECS[specId] ?? civilian[0]!;
      this.state.push({
        specId: spec.id,
        paint: rng.pick(spec.paints),
        locked: rng.chance(LOCKED_CHANCE),
        vehicle: null,
        vacant: spot.reserved === true || (noParking ? noParking(spot.pos) : false),
        health: null,
        damage: null,
      });
    }
  }

  get active(): number {
    return this.activeCount;
  }

  isVacant(spotIndex: number): boolean {
    return this.state[spotIndex]?.vacant ?? true;
  }

  /** Populate around the focus right now (nothing is on screen yet). `atStart` also clears the player's spawn. */
  fillInitial(atStart = false): void {
    this.refresh(true, atStart);
  }

  fixedUpdate(): void {
    this.step++;
    if (this.step % 20 === 0) this.refresh(false);
  }

  private refresh(initial: boolean, atStart = false): void {
    const f = this.focus();
    let spawnedThisTick = 0;
    for (let i = 0; i < this.spots.length; i++) {
      const spot = this.spots[i]!;
      const st = this.state[i]!;
      if (st.vacant) continue;
      const d = distance(spot.pos, f);
      const v = st.vehicle;
      if (v) {
        if (!this.vehicles.get(v.id)) {
          // Despawned by someone else.
          st.vehicle = null;
          st.vacant = true;
          this.activeCount--;
          continue;
        }
        if (this.hasLeft(v, spot)) {
          // Stolen, driven, pushed away or wrecked: the spot stays empty.
          delete v.data.parked;
          v.tags.delete('parked');
          st.vehicle = null;
          st.vacant = true;
          this.activeCount--;
          continue;
        }
        if (d > DESPAWN_R && !this.view.isVisible(spot.pos.x, spot.pos.z, 4)) this.streamOut(st, v);
        continue;
      }
      if (d > SPAWN_R || this.activeCount >= MAX_ACTIVE) continue;
      if (atStart && this.startPos && distance(spot.pos, this.startPos) < START_CLEAR_RADIUS) continue;
      if (!initial && (spawnedThisTick >= 3 || this.view.isVisible(spot.pos.x, spot.pos.z, 4))) continue;
      const spec = VEHICLE_SPECS[st.specId]!;
      if (this.vehicles.nearest(spot.pos, spec.length / 2 + 0.8)) continue; // sandbox / mission car already there
      const car = this.vehicles.spawn(st.specId, spot.pos, {
        heading: spot.heading,
        paint: st.paint,
        locked: st.locked,
        parked: true,
        tags: ['parked'],
      });
      car.data.parked = { spot: i };
      if (st.health !== null) car.health = st.health;
      if (st.damage) car.damage = { ...st.damage };
      st.vehicle = car;
      this.activeCount++;
      spawnedThisTick++;
    }
  }

  /** Focus jumped: stream out every car beyond the despawn radius now (nobody can see them any more). */
  releaseFar(): void {
    const f = this.focus();
    for (let i = 0; i < this.spots.length; i++) {
      const st = this.state[i]!;
      const v = st.vehicle;
      if (!v || !this.vehicles.get(v.id) || this.hasLeft(v, this.spots[i]!)) continue;
      if (distance(this.spots[i]!.pos, f) > DESPAWN_R) this.streamOut(st, v);
    }
  }

  private streamOut(st: SpotState, v: Vehicle): void {
    st.health = v.health;
    st.damage = { ...v.damage };
    this.vehicles.despawn(v);
    st.vehicle = null;
    this.activeCount--;
  }

  private hasLeft(v: Vehicle, spot: ParkedSpot): boolean {
    if (v.destroyed || v.controller !== 'none' || v.occupant !== null || v.ownedByPlayer || v.stolen) return true;
    return distance(v.pos, spot.pos) > 3.2;
  }

  dispose(): void {
    for (const st of this.state) {
      if (st.vehicle && this.vehicles.get(st.vehicle.id)) this.vehicles.despawn(st.vehicle);
      st.vehicle = null;
    }
    this.activeCount = 0;
  }
}
